import { gql } from '@apollo/client';
import { BlogInfoFragment } from '../../fragments/GeneralSettings';
import { NavigationMenu } from '../../components/NavigationMenu';
import { DEFAULT_BLOG_AUTHOR } from '../../constants/blog';
import { normalizeBlogAuthor, toFullAuthorName } from '../blogUtils';
import * as MENUS from '../../constants/menus';

/** Minimal front page query: menus + generalSettings. Use with fetchLogoPosts + GET_FEATURED_CASE_IMAGES_FROM_POSTS + fetchBlogListData for data. */
export const GET_FRONT_PAGE_DATA = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  query GetFrontPageData(
    $headerLocation: MenuLocationEnum
    $footerLocation: MenuLocationEnum
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    headerMenuItems: menuItems(where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }
`;

/** Full front page with logos, featured case images, recent blog (posts API). For wp-templates/front-page.js. */
export const GET_FRONT_PAGE_WITH_POSTS = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  query GetFrontPageWithPosts(
    $headerLocation: MenuLocationEnum
    $footerLocation: MenuLocationEnum
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    headerMenuItems: menuItems(where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    logoPosts: posts(where: { categoryName: "logos" }, first: 50) {
      nodes {
        title
        slug
        clientLogos {
          logoWhite {
            node {
              sourceUrl
            }
          }
        }
      }
    }
    recentEnPosts: posts(where: { categoryName: "blog" }, first: 3) {
      nodes {
        slug
        title
        excerpt
        featuredImage {
          node {
            sourceUrl
          }
        }
        author {
          node {
            name
          }
        }
      }
    }
    mehilainenFi: posts(where: { name: "mehilainen", categoryName: "tyomme" }, first: 1) {
      nodes {
        featuredImage {
          node {
            sourceUrl
          }
        }
        customerStory {
          headerImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
    vismaFi: posts(where: { name: "visma", categoryName: "tyomme" }, first: 1) {
      nodes {
        featuredImage {
          node {
            sourceUrl
          }
        }
        customerStory {
          headerImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
    vismaSiriusFi: posts(where: { name: "visma-sirius", categoryName: "tyomme" }, first: 1) {
      nodes {
        featuredImage {
          node {
            sourceUrl
          }
        }
        customerStory {
          headerImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
    smartumFi: posts(where: { name: "smartum", categoryName: "tyomme" }, first: 1) {
      nodes {
        featuredImage {
          node {
            sourceUrl
          }
        }
        customerStory {
          headerImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
  }
`;

/** Featured case study images from posts in category "tyomme" (posts API only). */
export const GET_FEATURED_CASE_IMAGES_FROM_POSTS = gql`
  query GetFeaturedCaseImagesFromPosts {
    mehilainenFi: posts(where: { name: "mehilainen", categoryName: "tyomme" }, first: 1) {
      nodes {
        featuredImage {
          node {
            sourceUrl
          }
        }
        customerStory {
          headerImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
    vismaFi: posts(where: { name: "visma", categoryName: "tyomme" }, first: 1) {
      nodes {
        featuredImage {
          node {
            sourceUrl
          }
        }
        customerStory {
          headerImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
    vismaSiriusFi: posts(where: { name: "visma-sirius", categoryName: "tyomme" }, first: 1) {
      nodes {
        featuredImage {
          node {
            sourceUrl
          }
        }
        customerStory {
          headerImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
    smartumFi: posts(where: { name: "smartum", categoryName: "tyomme" }, first: 1) {
      nodes {
        featuredImage {
          node {
            sourceUrl
          }
        }
        customerStory {
          headerImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
  }
`;

export const FRONT_PAGE_VARIABLES = {
  headerLocation: MENUS.PRIMARY_LOCATION,
  footerLocation: MENUS.FOOTER_LOCATION,
};

/**
 * Maps logo nodes (from clients CPT or logoPosts) to hero logos. Prefer clients when both exist.
 * @param {object} data - Query result with optional clients.nodes and/or logoPosts.nodes
 * @returns {Array<{ name, slug, logoUrl }>} Up to 8 logos, reversed order
 */
export function mapLogoPostsToLogos(nodes) {
  if (!nodes?.length) return [];
  const mapped = nodes.map((node) => ({
    name: node?.title ?? '',
    slug: node?.slug ?? '',
    logoUrl: node?.clientLogos?.logoWhite?.node?.sourceUrl ?? null,
  }));
  return [...mapped].reverse().slice(0, 8);
}

export function getLogoNodesFromPageData(data) {
  return data?.clients?.nodes ?? data?.logoPosts?.nodes ?? [];
}

/** Recent blog post nodes for front page preview. Prefer blogs CPT; lang is 'en' or 'fi'. */
export function getRecentPostNodesFromPageData(data, lang) {
  if (lang === 'fi') {
    return data?.recentFiBlogs?.nodes ?? data?.recentFiPosts?.nodes ?? [];
  }
  return data?.recentEnBlogs?.nodes ?? data?.recentEnPosts?.nodes ?? [];
}

function getCaseImageFromNode(node) {
  return (
    node?.customerStory?.headerImage?.node?.sourceUrl ??
    node?.featuredImage?.node?.sourceUrl ??
    null
  );
}

/** Image URLs for Mehiläinen, Visma Sirius, Smartum (etusivu/Palvelut). Uses customerStory.headerImage or featuredImage. */
export function getFeaturedCaseImageUrls(data) {
  const mehilainen = getCaseImageFromNode(data?.mehilainenFi?.nodes?.[0]);
  const visma =
    getCaseImageFromNode(data?.vismaFi?.nodes?.[0]) ??
    getCaseImageFromNode(data?.vismaSiriusFi?.nodes?.[0]);
  const smartum = getCaseImageFromNode(data?.smartumFi?.nodes?.[0]);
  return { mehilainen, visma, smartum };
}

/** @deprecated Use getFeaturedCaseImageUrls(data).smartum instead. */
export function getSmartumCardImageUrl(data) {
  const urls = getFeaturedCaseImageUrls(data);
  return urls.smartum ?? null;
}

/**
 * @param {Array} nodes - Post nodes from recentEnPosts
 * @param {string} [blogBasePath] - Base path for links, e.g. '/blog' or '/blogi'
 */
export function mapRecentPostsToPreview(nodes, blogBasePath = '/blog') {
  if (!nodes?.length) return [];
  const base = blogBasePath.replace(/\/$/, '');
  return nodes.map((node) => {
    const image = node?.featuredImage?.node?.sourceUrl ?? null;
    const author = toFullAuthorName(normalizeBlogAuthor(node?.author?.node?.name) || DEFAULT_BLOG_AUTHOR);
    const excerpt =
      typeof node?.excerpt === 'string'
        ? node.excerpt.replace(/<[^>]+>/g, '').trim()
        : '';
    return {
      slug: node.slug,
      title: node.title ?? '',
      excerpt: excerpt || undefined,
      image,
      author,
      href: `${base}/${node.slug}`,
    };
  });
}
